class Api {
  constructor() {
    this.language = document.documentElement.lang
    this.base = '/api/'

    document.addEventListener('DOMContentLoaded', () => {
      this.csrf = document.getElementsByName('csrfmiddlewaretoken')[0]?.value
      // this.loader = document.querySelector('#loader')
    })
  }

  request(path, opts, base) {
    return fetch((base ? base : this.base) + path, opts)
      .then((response) => {
        if (response.ok) return response

        return Promise.reject(response)
      })
      .catch(async (response) => {
        let error

        try {
          error = await response.json()
        } catch (error) {
          // console.log(error)
          return Promise.reject(new Error('Eroare necunoscută'))
        }

        const message = error.message
          ? 'Eventbook: ' + error.message
          : error.detail ||
            error.error ||
            (typeof error === 'string' ? error : null)
        return Promise.reject(
          new Error(
            typeof message === 'string' ? message : 'Eroare necunoscută',
          ),
        )
      })
  }

  post(path, body, opts) {
    return this.request(path, {
      ...opts,
      body: JSON.stringify(body),
      method: 'POST',
      headers: {
        'X-CSRFToken': this.csrf,
        'Content-Type': 'application/json',
      },
    }).then(this.processResponse)
  }

  patch(path, opts) {
    return this.request(path, {
      ...opts,
      method: 'PATCH',
      headers: {
        'X-CSRFToken': this.csrf,
        'Content-Type': 'application/json',
      },
    })
  }

  delete(path, opts) {
    return this.request(path, {
      ...opts,
      method: 'DELETE',
      headers: {
        'X-CSRFToken': this.csrf,
      },
    })
  }

  get(path, opts) {
    return this.request(path, opts).then(this.processResponse)
  }

  async processResponse(response) {
    if (!response.ok) {
      return response.text()
    }

    const text = await response.text()
    if (!text) {
      return null
    }

    // Try to parse as JSON, fallback to text if it fails
    try {
      return JSON.parse(text)
    } catch (e) {
      return text
    }
  }

  // Misc.
  toggleFollow(body) {
    const path = 'favorites/toggle/'
    return this.post(path, body)
  }

  search(query) {
    return this.get('search/?' + query)
  }

  getIndex(path, params) {
    return this.get(path + '?' + new URLSearchParams(params).toString())
  }

  // TICKET section

  getCartData(id) {
    return this.get('cart/' + (id ? id + '/' : ''))
  }

  getReservations() {
    return this.get('cart/reservation/')
  }

  getPerformance(id) {
    return this.get(`performance/${id}/`)
  }

  getPerformances(id) {
    return this.get('cart/' + (id ? id + '/old_' : '') + 'performances/')
  }

  getPasses() {
    return this.get('cart/list_passes/')
  }

  getHallSVG(id) {
    return this.request(`performance-hall-svg/${id}/`, {}, '/').then(
      (response) => response.text(),
    )
  }

  getPaymentMethods() {
    return this.get('cart/get_payment_methods/').then((response) =>
      response.sort((a, b) => (a.id > b.id ? 1 : -1)),
    )
  }

  addTicket(params) {
    if (params.pass_code) {
      return this.post('cart/add_ticket_with_pass/', params)
    } else if (params.price_category_id || params.seat_id) {
      return this.post('cart/add_ticket/', params)
    }
  }

  updateTickets(params) {
    if (params.pass_code) {
      return this.post('cart/update_tickets_with_pass/', params)
    } else {
      return this.post('cart/update_tickets/', params)
    }
  }

  updateSeat(params) {
    return this.post('cart/update_ticket_with_seat/', params)
  }

  updatePaymentMethod(params) {
    return this.post('cart/update_transaction_payment_delivery/', params)
  }

  checkPass(params) {
    return this.post('cart/check_pass/', params)
  }

  removeTicket(params) {
    return this.post('cart/remove_ticket/', params)
  }

  clearPerformanceTickets(params) {
    return this.post('cart/clear_performance_tickets/', params)
  }

  clearSession() {
    return this.get('cart/clear/')
  }

  payTransaction() {
    return this.get('cart/pay/')
  }

  payIncognito(params) {
    return this.post('cart/pay_incognito/', params)
  }

  applyDiscount(code) {
    return this.post('cart/apply_discount/', { code })
  }

  // Management section

  getDashboard() {
    return this.get('management/dashboard/')
  }

  getEvents(cinemaId, year, weekNumber) {
    const path = `management/events/${cinemaId || ''}/${year}/${
      weekNumber + '/' || ''
    }`
    return this.get(path)
  }

  addDailyPersonel(data) {
    return this.post('management/add-daily-personel/', data)
  }

  addDefaultDailyPersonel(data) {
    return this.post('management/add-default-personel/', data)
  }

  removeDailyPersonel(id) {
    return this.delete(`management/remove-daily-personel/${id}/`)
  }

  addEventPersonel(eventType, data) {
    return this.post(`management/add-event-personel/${eventType}/`, data)
  }

  removeEventPersonel(eventType, id) {
    return this.delete(`management/remove-event-personel/${eventType}/${id}/`)
  }

  addVolunteerToEvent(eventType, data) {
    return this.post(`management/add-volunteer-to-event/${eventType}/`, data)
  }

  removeVolunteerFromEvent(eventType, id) {
    return this.delete(
      `management/remove-volunteer-from-event/${eventType}/${id}/`,
    )
  }

  addEventVolunteerRole(eventType, data) {
    return this.post(`management/add-event-volunteer-role/${eventType}/`, data)
  }

  removeVolunteerRoleFromEvent(eventType, id, userId) {
    let path = `management/remove-volunteer-role-from-event/${eventType}/`
    if (id) path += `${id}/`
    if (userId) path += `${userId}/`
    return this.delete(path)
  }

  // Staff reports

  getUserActivityLog(userId, year, month) {
    let url = `management/user-activity-log/${userId}/`
    const params = {}
    if (year) params.year = year
    if (month) params.month = month
    const query = Object.keys(params).length
      ? '?' + new URLSearchParams(params).toString()
      : ''

    return this.get(url + query)
  }

  getUserActivityMonthly(userId, year) {
    return this.get(
      `management/user-activity-log-stats/${userId}/?year=${year}`,
    )
  }

  subscribeNewsletter(email, recaptchaToken = null) {
    return this.post('newsletter/subscribe/', {
      email,
      recaptcha_token: recaptchaToken,
    })
  }

  getMonthlyOverview(year, month, type = null) {
    const typeParam = type ? `&type=${type}` : ''
    return this.get(
      `management/monthly-overview/?year=${year}&month=${month}${typeParam}`,
    )
  }

  getMonthlyOverviewExportUrl(year, month, type = null) {
    const typeParam = type ? `&type=${type}` : ''
    return `/api/management/monthly-overview-export/?year=${year}&month=${month}${typeParam}`
  }
}

export default new Api()
